import type { Id } from "@Aspecto/backend/convex/_generated/dataModel";

import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useAction, useMutation as useConvexDirectMutation } from "convex/react";
import {
  Calendar,
  ChevronDown,
  FileText,
  Globe,
  Mail,
  Newspaper,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { CreateNewsletterDialog } from "@/components/newsletters/create-newsletter-dialog";
import { NewsletterViewDialog } from "@/components/newsletters/newsletter-view";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropdownSelect } from "@/components/ui/dropdown-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { Switch } from "@/components/ui/switch";
import { useGroupLabel } from "@/hooks/use-group-label";
import { formatDigestDate, getInitials } from "@/lib/format";
import { useCurrentUser, useCurrentCompany } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/admin/communication")({
  component: CommunicationSettings,
});

const LEGACY_DIGEST_FEATURE_ENABLED = false;
const WEEKLY_DIGEST_DEBUG_ENABLED = false;

type DigestFrequency = "weekly" | "biweekly" | "monthly" | "quarterly";
type DigestWeekday =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";
type DigestSectionKey = "upcomingEvents" | "latestAnnouncements" | "discoverCommunities";
type DigestSections = Record<DigestSectionKey, boolean>;

const DIGEST_FREQUENCY_OPTIONS: { value: DigestFrequency; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

const DIGEST_WEEKDAY_OPTIONS: { value: DigestWeekday; label: string }[] = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const DEFAULT_DIGEST_SECTIONS: DigestSections = {
  upcomingEvents: true,
  latestAnnouncements: true,
  discoverCommunities: true,
};

interface AdminGroup {
  _id: Id<"groups">;
  name: string;
  isGlobal: boolean;
}

interface DigestItem {
  _id: Id<"newsletters">;
  title: string;
  description?: string;
  groupName: string;
  timePeriod: string;
  sentAt?: number;
  creator?: {
    firstName?: string;
    lastName?: string;
    imageUrl?: string;
  } | null;
}

interface DigestToggleProps {
  digestEnabled: boolean;
  digestFrequency: DigestFrequency;
  digestWeekday: DigestWeekday;
  digestSections: DigestSections;
}

interface DigestEmailPreview {
  subject: string;
  html: string;
  text: string;
  generatedAt: number;
}

interface ScheduledDigestHistoryItem {
  _id: string;
  subject: string;
  html: string;
  text: string;
  deliveryKey: string;
  recipientName: string;
  recipientEmail: string;
  sentAt: number;
}

interface DigestSectionProps {
  companyId: Id<"companies">;
  digestEnabled: boolean;
}

interface DigestDebugResponse {
  nowIso: string;
  configuredWindows: string[];
  user: {
    userId: Id<"users">;
    email?: string;
    name: string;
    timezone: string;
  };
  company: {
    companyId: Id<"companies">;
    name: string;
    digestEnabled: boolean;
    digestFrequency: DigestFrequency;
    digestWeekday: DigestWeekday;
  };
  schedule: {
    localTime: string;
    deliveryKey: string;
    isDueNow: boolean;
    alreadySentForPeriod: boolean;
  };
  eligibility: {
    candidateStatus:
      | "eligible"
      | "company_digest_disabled"
      | "user_digest_disabled"
      | "missing_email";
    notifyWeeklyDigest: boolean;
    hasEmail: boolean;
  };
  automaticStatus:
    | "would_send"
    | "not_due"
    | "already_sent"
    | "no_payload"
    | "company_digest_disabled"
    | "user_digest_disabled"
    | "missing_email";
  payload: {
    exists: boolean;
    upcomingEventsCount: number;
    latestAnnouncementsCount: number;
    discoverCommunitiesCount: number;
  };
  send: {
    attempted: boolean;
    sent: boolean;
    skippedReason:
      | "eligible"
      | "company_digest_disabled"
      | "user_digest_disabled"
      | "missing_email"
      | "no_payload"
      // Digest had nothing to report, so it was not emailed.
      | "empty"
      | null;
    emailId: string | null;
    recordedDelivery: boolean;
  };
}

interface GroupSelectorProps {
  groups: AdminGroup[];
  selectedGroupId: Id<"groups"> | null;
  onSelect: (groupId: Id<"groups">) => void;
}

interface DigestCardProps {
  digest: DigestItem;
  onView: () => void;
  onDelete: () => void;
}

interface DeleteDigestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

function GroupIcon({ isGlobal }: { isGlobal: boolean }) {
  const Icon = isGlobal ? Globe : Users;
  return <Icon className="h-4 w-4 text-muted-foreground" />;
}

function CommunicationSettings() {
  const { data: company } = useCurrentCompany();

  if (!company) return null;

  const digestEnabled = company.digestEnabled !== false;
  const digestFrequency = company.digestFrequency ?? "weekly";
  const digestWeekday = company.digestWeekday ?? "tuesday";
  const digestSections = company.digestSections ?? DEFAULT_DIGEST_SECTIONS;

  return (
    <div className="space-y-6">
      <DigestToggle
        digestEnabled={digestEnabled}
        digestFrequency={digestFrequency}
        digestWeekday={digestWeekday}
        digestSections={digestSections}
      />
      <ScheduledDigestHistorySection />
      {WEEKLY_DIGEST_DEBUG_ENABLED ? <DigestDebugSection /> : null}
      {LEGACY_DIGEST_FEATURE_ENABLED ? (
        <DigestSection companyId={company._id} digestEnabled={digestEnabled} />
      ) : null}
    </div>
  );
}

function DigestToggle({
  digestEnabled,
  digestFrequency,
  digestWeekday,
  digestSections,
}: DigestToggleProps) {
  const groupLabel = useGroupLabel();
  const updateDigestEnabled = useConvexDirectMutation(api.companies.updateDigestEnabled);
  const updateDigestFrequency = useConvexDirectMutation(api.companies.updateDigestFrequency);
  const updateDigestWeekday = useConvexDirectMutation(api.companies.updateDigestWeekday);
  const updateDigestSections = useConvexDirectMutation(api.companies.updateDigestSections);
  const previewWeeklyDigestEmail = useAction(api.weeklyDigests.previewWeeklyDigestEmail);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<DigestEmailPreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const handleToggle = async (enabled: boolean) => {
    try {
      await updateDigestEnabled({ enabled });
      toast.success(`Digest ${enabled ? "enabled" : "disabled"} successfully`);
    } catch {
      toast.error("Failed to update Digest settings");
    }
  };

  const handleFrequencyChange = async (frequency: DigestFrequency) => {
    try {
      await updateDigestFrequency({ frequency });
      toast.success("Digest frequency updated successfully");
    } catch {
      toast.error("Failed to update Digest frequency");
    }
  };

  const handleWeekdayChange = async (weekday: DigestWeekday) => {
    try {
      await updateDigestWeekday({ weekday });
      toast.success("Digest send day updated successfully");
    } catch {
      toast.error("Failed to update Digest send day");
    }
  };

  const digestWeekdayLabel =
    DIGEST_WEEKDAY_OPTIONS.find((option) => option.value === digestWeekday)?.label ?? "Tuesday";
  const selectedSectionCount = Object.values(digestSections).filter(Boolean).length;
  const digestSectionOptions: {
    key: DigestSectionKey;
    title: string;
    description: string;
    icon: typeof Calendar;
  }[] = [
    {
      key: "upcomingEvents",
      title: "Upcoming events",
      description: "Include events scheduled in the digest period.",
      icon: Calendar,
    },
    {
      key: "latestAnnouncements",
      title: "Latest announcements",
      description: `Include recent posts and updates from joined ${groupLabel.pluralLower}.`,
      icon: FileText,
    },
    {
      key: "discoverCommunities",
      title: `Discover ${groupLabel.pluralLower}`,
      description: `Include newly created ${groupLabel.pluralLower} members can join.`,
      icon: Users,
    },
  ];

  const handleSectionChange = async (sectionKey: DigestSectionKey, included: boolean) => {
    if (!included && selectedSectionCount === 1 && digestSections[sectionKey]) {
      toast.error("At least one digest section must be selected");
      return;
    }

    const nextSections = {
      ...digestSections,
      [sectionKey]: included,
    };

    try {
      await updateDigestSections(nextSections);
      toast.success("Digest sections updated successfully");
    } catch (error) {
      toast.error("Failed to update Digest sections", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    }
  };

  const handlePreviewEmail = async () => {
    setPreviewOpen(true);
    setPreview(null);
    setIsPreviewLoading(true);

    try {
      setPreview(await previewWeeklyDigestEmail({}));
    } catch (error) {
      toast.error("Failed to load digest email preview", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  return (
    <>
      <SectionCard title="Digest" description="Manage digest emails for your organization">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="digestEnabled">Enable Digest</Label>
            <p className="text-xs text-muted-foreground">
              When enabled, digest email is sent at 9am in each member's saved time zone.
            </p>
          </div>
          <Switch
            id="digestEnabled"
            checked={digestEnabled}
            onCheckedChange={handleToggle}
            disabled={false}
          />
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="digestFrequency">Frequency</Label>
            <DropdownSelect
              id="digestFrequency"
              value={digestFrequency}
              options={DIGEST_FREQUENCY_OPTIONS}
              onValueChange={(value) => void handleFrequencyChange(value as DigestFrequency)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="digestWeekday">Send day</Label>
            <DropdownSelect
              id="digestWeekday"
              value={digestWeekday}
              options={DIGEST_WEEKDAY_OPTIONS}
              onValueChange={(value) => void handleWeekdayChange(value as DigestWeekday)}
            />
          </div>
        </div>
        <div className="mt-2">
          <p className="text-xs text-muted-foreground">
            Monthly and quarterly digests send on the first {digestWeekdayLabel} of the period.
          </p>
        </div>
        <div className="mt-5 grid gap-3">
          <Label>Included content</Label>
          <div className="grid gap-3">
            {digestSectionOptions.map((option) => {
              const Icon = option.icon;
              const isChecked = digestSections[option.key];
              const isLastSelected = isChecked && selectedSectionCount === 1;

              return (
                <div key={option.key} className="flex items-start gap-3 rounded-lg border p-3">
                  <Checkbox
                    id={`digest-${option.key}`}
                    checked={isChecked}
                    disabled={isLastSelected}
                    onCheckedChange={(checked) =>
                      void handleSectionChange(option.key, checked === true)
                    }
                  />
                  <div className="grid gap-1 leading-none">
                    <Label
                      htmlFor={`digest-${option.key}`}
                      className={`flex items-center gap-2 ${isLastSelected ? "cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <Icon className="h-4 w-4" />
                      {option.title}
                    </Label>
                    <p className="text-xs leading-5 text-muted-foreground">{option.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            variant="outline"
            onClick={() => void handlePreviewEmail()}
            disabled={isPreviewLoading}
          >
            <Mail />
            {isPreviewLoading ? "Loading preview..." : "Preview email"}
          </Button>
        </div>
      </SectionCard>
      <DigestEmailPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        preview={preview}
        isLoading={isPreviewLoading}
      />
    </>
  );
}

function ScheduledDigestHistorySection() {
  const { data: history, isLoading } = useQuery(
    convexQuery(api.weeklyDigests.listScheduledDigestHistory, {}),
  );
  const [selectedDigest, setSelectedDigest] = useState<ScheduledDigestHistoryItem | null>(null);

  const preview = selectedDigest
    ? {
        subject: selectedDigest.subject,
        html: selectedDigest.html,
        text: selectedDigest.text,
        generatedAt: selectedDigest.sentAt,
      }
    : null;

  return (
    <>
      <SectionCard
        title="Past scheduled digests"
        description="View the last 10 scheduled digest emails sent by your organization"
      >
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-20 animate-pulse rounded-lg border bg-muted/40" />
            ))}
          </div>
        ) : !history || history.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8">
            <Mail className="mb-3 h-10 w-10 text-muted-foreground/60" />
            <p className="text-sm text-muted-foreground">
              No scheduled digests have been sent yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((digest) => (
              <button
                key={digest._id}
                type="button"
                onClick={() => setSelectedDigest(digest)}
                className="flex w-full items-start justify-between gap-4 rounded-lg border p-4 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0 text-primary" />
                    <p className="truncate text-sm font-semibold">{digest.subject}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Snapshot from {digest.recipientName || digest.recipientEmail}
                  </p>
                </div>
                <div className="shrink-0 text-right text-xs text-muted-foreground">
                  <p>{formatDigestDate(digest.sentAt)}</p>
                  <p>{digest.deliveryKey}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      <DigestEmailPreviewDialog
        open={selectedDigest !== null}
        onOpenChange={(open) => !open && setSelectedDigest(null)}
        preview={preview}
        isLoading={false}
      />
    </>
  );
}

function DigestEmailPreviewDialog({
  open,
  onOpenChange,
  preview,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: DigestEmailPreview | null;
  isLoading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-[calc(100%-2rem)] flex-col gap-4 p-0 sm:max-w-5xl">
        <DialogHeader className="border-b px-5 pt-5 pb-4">
          <DialogTitle>Digest email preview</DialogTitle>
          <DialogDescription>
            {preview?.subject
              ? `Subject: ${preview.subject}`
              : "Preview the scheduled digest email."}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 px-5 pb-5">
          {isLoading ? (
            <div className="flex h-full min-h-96 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
              Loading preview...
            </div>
          ) : preview ? (
            <iframe
              title="Digest email preview"
              srcDoc={preview.html}
              sandbox=""
              className="h-full min-h-96 w-full rounded-md border bg-white"
            />
          ) : (
            <div className="flex h-full min-h-96 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
              Preview unavailable.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DigestDebugSection() {
  const { data: currentUser } = useCurrentUser();
  const { data: companyMembers } = useQuery(convexQuery(api.companies.getCompanyMembers, {}));
  const debugWeeklyDigest = useAction(api.weeklyDigests.debugWeeklyDigest);

  const [selectedUserId, setSelectedUserId] = useState<Id<"users"> | null>(null);
  const [result, setResult] = useState<DigestDebugResponse | null>(null);
  const [pendingMode, setPendingMode] = useState<"check" | "send" | null>(null);

  useEffect(() => {
    if (!companyMembers || companyMembers.length === 0) return;

    setSelectedUserId((currentValue) => {
      if (currentValue && companyMembers.some((member) => member._id === currentValue)) {
        return currentValue;
      }

      return currentUser?._id ?? companyMembers[0]!._id;
    });
  }, [companyMembers, currentUser?._id]);

  const selectedUser = companyMembers?.find((member) => member._id === selectedUserId) ?? null;
  const memberOptions =
    companyMembers?.map((member) => {
      const memberName =
        `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() || member.email;

      return {
        value: member._id,
        label: `${memberName} ${member.email ? `(${member.email})` : "(no email)"}`,
      };
    }) ?? [];

  const runDebug = async (forceSend: boolean) => {
    if (!selectedUserId) return;

    setPendingMode(forceSend ? "send" : "check");

    try {
      const nextResult = await debugWeeklyDigest({
        userId: selectedUserId,
        forceSend,
      });

      setResult(nextResult);

      if (forceSend) {
        if (nextResult.send.sent) {
          toast.success("Test digest sent", {
            description: nextResult.send.emailId ?? nextResult.user.email ?? "Email accepted",
          });
        } else {
          toast.error("Test digest was not sent", {
            description: nextResult.send.skippedReason ?? "Unknown reason",
          });
        }
      } else {
        toast.success("Digest status checked");
      }
    } catch (error) {
      toast.error("Failed to inspect weekly digest", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setPendingMode(null);
    }
  };

  return (
    <SectionCard
      title="Weekly Digest Debug"
      description="Inspect one member's digest state now or force-send a test digest without waiting for the next cron window."
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="digest-debug-user">Select member</Label>
          <DropdownSelect
            id="digest-debug-user"
            value={selectedUserId ?? ""}
            options={memberOptions}
            onValueChange={(value) => setSelectedUserId(value as Id<"users">)}
            placeholder="Select member"
          />
        </div>

        <p className="text-xs text-muted-foreground">
          Force send bypasses the current Tuesday time window and does not record a delivery for the
          period, so you can test repeatedly without waiting for cron.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => void runDebug(false)}
            disabled={!selectedUserId || pendingMode !== null}
          >
            {pendingMode === "check" ? "Checking..." : "Check now"}
          </Button>
          <Button
            variant="outline"
            onClick={() => void runDebug(true)}
            disabled={!selectedUserId || pendingMode !== null}
          >
            {pendingMode === "send" ? "Sending..." : "Force send test digest"}
          </Button>
        </div>

        {selectedUser && (
          <div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
            Testing as <span className="font-medium text-foreground">{selectedUser.email}</span>
            {selectedUser.timezone ? ` in ${selectedUser.timezone}` : " with no saved timezone"}
          </div>
        )}

        {result && (
          <Card className="border-dashed">
            <CardContent className="space-y-4 pt-6">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <p className="data-label">Automatic status</p>
                  <Badge variant="secondary">{result.automaticStatus}</Badge>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Eligibility</p>
                  <Badge variant="outline">{result.eligibility.candidateStatus}</Badge>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Local time</p>
                  <p className="text-sm">{result.schedule.localTime}</p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Configured windows</p>
                  <p className="text-sm">{result.configuredWindows.join(", ")}</p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Due now</p>
                  <p className="text-sm">{result.schedule.isDueNow ? "Yes" : "No"}</p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Frequency</p>
                  <p className="text-sm">{result.company.digestFrequency}</p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Send day</p>
                  <p className="text-sm">{result.company.digestWeekday}</p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Delivery key</p>
                  <p className="text-sm">{result.schedule.deliveryKey}</p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Already sent this period</p>
                  <p className="text-sm">{result.schedule.alreadySentForPeriod ? "Yes" : "No"}</p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Payload sections</p>
                  <p className="text-sm">
                    Events {result.payload.upcomingEventsCount}, updates{" "}
                    {result.payload.latestAnnouncementsCount}, communities{" "}
                    {result.payload.discoverCommunitiesCount}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="data-label">Last send attempt</p>
                  <p className="text-sm">
                    {result.send.sent
                      ? `Sent${result.send.emailId ? ` (${result.send.emailId})` : ""}`
                      : result.send.skippedReason
                        ? `Skipped: ${result.send.skippedReason}`
                        : "No forced send"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </SectionCard>
  );
}

function GroupSelector({ groups, selectedGroupId, onSelect }: GroupSelectorProps) {
  const groupLabel = useGroupLabel();
  const selectedGroup = groups.find((g) => g._id === selectedGroupId);

  return (
    <div className="space-y-2">
      <Label>Select {groupLabel.singularLower}</Label>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <span className="flex items-center gap-2">
            <GroupIcon isGlobal={selectedGroup?.isGlobal ?? false} />
            {selectedGroup?.name ?? `Select a ${groupLabel.singularLower}`}
            {selectedGroup?.isGlobal && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                Global
              </Badge>
            )}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[var(--anchor-width)]">
          <DropdownMenuRadioGroup
            value={selectedGroupId ?? ""}
            onValueChange={(value) => onSelect(value as Id<"groups">)}
          >
            {groups.map((group) => (
              <DropdownMenuRadioItem key={group._id} value={group._id}>
                <span className="flex items-center gap-2">
                  <GroupIcon isGlobal={group.isGlobal} />
                  {group.name}
                  {group.isGlobal && (
                    <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                      Global
                    </Badge>
                  )}
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function DigestCard({ digest, onView, onDelete }: DigestCardProps) {
  return (
    <Card className="cursor-pointer transition-colors hover:bg-accent/50" onClick={onView}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Newspaper className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">{digest.title}</h3>
            </div>
            {digest.description && (
              <p className="line-clamp-1 text-xs text-muted-foreground">{digest.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {digest.groupName}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {digest.timePeriod === "week"
                ? "Weekly"
                : digest.timePeriod === "month"
                  ? "Monthly"
                  : "Upcoming"}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {digest.creator && (
              <>
                <Avatar className="h-4 w-4">
                  <AvatarImage src={digest.creator.imageUrl} />
                  <AvatarFallback className="text-[8px]">
                    {getInitials(digest.creator.firstName, digest.creator.lastName)}
                  </AvatarFallback>
                </Avatar>
                <span>
                  {digest.creator.firstName} {digest.creator.lastName}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span>{formatDigestDate(digest.sentAt)}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DigestListSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader className="space-y-2">
            <div className="h-5 w-3/4 rounded bg-muted" />
            <div className="h-4 w-1/2 rounded bg-muted" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function EmptyDigests() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8">
      <Newspaper className="mb-3 h-10 w-10 text-muted-foreground/60" />
      <p className="text-sm text-muted-foreground">
        No digests yet. Create your first digest to share updates.
      </p>
    </div>
  );
}

function DeleteDigestDialog({
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
}: DeleteDigestDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete digest</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete this digest and its related notifications. This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" disabled={isDeleting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DigestSection({ companyId, digestEnabled }: DigestSectionProps) {
  const { data: digests, isLoading } = useQuery(convexQuery(api.newsletters.getCompanyDigests, {}));
  const { data: adminGroups } = useQuery(convexQuery(api.newsletters.getAdminGroups, {}));
  const groupLabel = useGroupLabel();

  const [viewingDigestId, setViewingDigestId] = useState<Id<"newsletters"> | null>(null);
  const [deletingDigestId, setDeletingDigestId] = useState<Id<"newsletters"> | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<Id<"groups"> | null>(null);

  const { mutate: deleteDigest, isPending: isDeleting } = useMutation({
    mutationFn: useConvexMutation(api.newsletters.deleteNewsletter),
    onSuccess: () => {
      toast.success("Digest deleted successfully");
      setDeletingDigestId(null);
    },
    onError: () => {
      toast.error("Failed to delete digest");
      setDeletingDigestId(null);
    },
  });

  useEffect(() => {
    if (adminGroups && adminGroups.length > 0 && !selectedGroupId) {
      setSelectedGroupId(adminGroups[0]._id);
    }
  }, [adminGroups, selectedGroupId]);

  if (!adminGroups || adminGroups.length === 0) return null;

  return (
    <>
      <SectionCard
        title="Digest"
        description={`Create and manage digests for your ${groupLabel.pluralLower}`}
      >
        <div className="space-y-4">
          {adminGroups.length > 1 && (
            <GroupSelector
              groups={adminGroups}
              selectedGroupId={selectedGroupId}
              onSelect={setSelectedGroupId}
            />
          )}

          {selectedGroupId && digestEnabled && (
            <div className="flex justify-end">
              <CreateNewsletterDialog groupId={selectedGroupId} companyId={companyId} />
            </div>
          )}

          {!digestEnabled && (
            <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              Digests are currently disabled. Enable them above to create new digests.
            </div>
          )}

          {isLoading ? (
            <DigestListSkeleton />
          ) : !digests || digests.length === 0 ? (
            <EmptyDigests />
          ) : (
            <div className="space-y-3">
              {digests.map((digest) => (
                <DigestCard
                  key={digest._id}
                  digest={digest}
                  onView={() => setViewingDigestId(digest._id)}
                  onDelete={() => setDeletingDigestId(digest._id)}
                />
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      <NewsletterViewDialog
        newsletterId={viewingDigestId}
        onClose={() => setViewingDigestId(null)}
      />

      <DeleteDigestDialog
        open={!!deletingDigestId}
        onOpenChange={(open) => !open && setDeletingDigestId(null)}
        onConfirm={() => deletingDigestId && deleteDigest({ newsletterId: deletingDigestId })}
        isDeleting={isDeleting}
      />
    </>
  );
}
